import { PlusIcon } from '@persona-id/design-tokens/icons';
import { useNavigate } from 'react-router-dom-v5-compat';

import PaginationV2 from 'dashboard/components/PaginationV2/PaginationV2';
import MultiSelect, { useLabelTextMapper } from 'dashboard/components-lib/MultiSelect/MultiSelect';
import { Container, Header, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import {
  GraphQueryTemplate,
  GraphQueryTemplateId,
  GraphQueryTemplateStatus,
} from 'dashboard/models/graph-query-template';
import { Filters } from 'dashboard/redux/actions/graph-query-templates';
import GraphQueryTemplatesTable from 'dashboard/views/DashboardGraphQueryTemplatesView/GraphQueryTemplatesTable';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import { HStack } from 'design-system/components/Stack';
import { PaginationParams } from 'lib/interfaces/api';

const STATUS_FILTER_OPTIONS = [
  {
    label: 'Active',
    value: GraphQueryTemplateStatus.Active,
  },
  {
    label: 'Archived',
    value: GraphQueryTemplateStatus.Archived,
  },
];

interface DashboardGraphQueryTemplatesViewProps {
  archiveGraphQueryTemplate: (id: GraphQueryTemplateId) => void;
  filters: Filters;
  graphQueryTemplates: GraphQueryTemplate[];
  handleFiltersStatusUpdate: (filters: string[]) => void;
  handlePageLinkClick: (pageNumber: number) => void;
  handlePageSizeClick: (pageSize: number) => void;
  handleSortUpdate: (sortParams: SortParams) => void;
  isLoadingGraphQueryTemplates: boolean;
  pageSize: number;
  paginationParams: PaginationParams;
  restoreGraphQueryTemplate: (id: GraphQueryTemplateId) => void;
  sortParams: SortParams;
}

const DashboardGraphQueryTemplatesView = ({
  archiveGraphQueryTemplate,
  filters,
  graphQueryTemplates,
  handleFiltersStatusUpdate,
  handlePageLinkClick,
  handlePageSizeClick,
  handleSortUpdate,
  isLoadingGraphQueryTemplates,
  pageSize,
  paginationParams,
  restoreGraphQueryTemplate,
  sortParams,
}: DashboardGraphQueryTemplatesViewProps) => {
  const navigate = useNavigate();

  return (
    <Container>
      <Header title='Graph templates'>
        <HStack>
          <Button
            variant='basic'
            prefixIcon={<PlusIcon />}
            onClick={() => navigate('/dashboard/explorer')}
          >
            Create template
          </Button>
        </HStack>
      </Header>
      <Section>
        <Card>
          <Card.Body>
            <Card.Title>Status</Card.Title>
            <MultiSelect
              value={filters.status || ''}
              labelTextMapper={useLabelTextMapper(STATUS_FILTER_OPTIONS, 'Statuses', {
                emptyIsAll: true,
              })}
              selectOptionValues={STATUS_FILTER_OPTIONS}
              onChange={handleFiltersStatusUpdate}
            />
          </Card.Body>
        </Card>
        <Card>
          <Card.Body>
            <div>
              <Card.Title>Query Template List</Card.Title>
            </div>
            <GraphQueryTemplatesTable
              isLoadingGraphQueryTemplates={isLoadingGraphQueryTemplates}
              graphQueryTemplates={graphQueryTemplates}
              sortParams={sortParams}
              archiveGraphQueryTemplate={archiveGraphQueryTemplate}
              restoreGraphQueryTemplate={restoreGraphQueryTemplate}
              handleSortUpdate={handleSortUpdate}
            />
            {graphQueryTemplates.length > 0 && (
              <PaginationV2
                pageSize={pageSize}
                paginationParams={paginationParams}
                handlePageSizeClick={handlePageSizeClick}
                handlePageLinkClick={handlePageLinkClick}
              />
            )}
          </Card.Body>
        </Card>
      </Section>
    </Container>
  );
};

export default DashboardGraphQueryTemplatesView;
